import { useState, useMemo, memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Save, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import MultiImageUpload from '../components/MultiImageUpload'
import MDEditor from '@uiw/react-md-editor';
import MarkdownIt from 'markdown-it'
import { contentAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

const mdParser = new MarkdownIt()

const Mading = ({ initialData = {}, onSuccess }) => {
    const [saving, setSaving] = useState(false)
    const [content, setContent] = useState("")
    const [images, setImages] = useState([])
    const [myMadings, setMyMadings] = useState([])
    const [mode, setMode] = useState('list')
    const [editIndex, setEditIndex] = useState(null)
    const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {
            MadingDataJudul: '',
            MadingDataPembuat: '',
            MadingDataTanggal: '',
            MadingDataImage: [],
        }
    })

    // Upload gambar handler
    const handleMultiImageUpload = async (formData) => {
        try {
            const response = await contentAPI.uploadMadingImage(formData)
            if (!response?.data?.imageUrl) {
                throw new Error('Invalid response format')
            }
            setImages(prev => [...prev, response.data.imageUrl])
            return response.data.imageUrl
        } catch (error) {
            console.error('Upload error:', error)
            toast.error(error.response?.data?.message || 'Gagal mengunggah gambar')
            throw error
        }
    }

    // Handle remove image
    const handleRemoveMultiImage = (idx) => {
        if (window.confirm('Apakah Anda yakin ingin menghapus gambar ini?')) {
            setImages(prev => prev.filter((_, i) => i !== idx))
        }
    }

    // Reset form untuk tambah baru
    const handleAddNewMading = () => {
        reset({
            MadingDataJudul: '',
            MadingDataPembuat: '',
            MadingDataTanggal: '',
            MadingDataImage: [],
        })
        setContent('')
        setImages([])
        setEditIndex(null)
        setMode('new')
    }

    // Set form ke mode edit
    const handleEditMading = (item, idx) => {
        setValue('MadingDataJudul', item.MadingDataJudul)
        setValue('MadingDataPembuat', item.MadingDataPembuat)
        setValue('MadingDataTanggal', item.MadingDataTanggal?.slice(0,10))
        setContent(item.MadingDataIsi)
        setImages(item.MadingDataImage || [])
        setEditIndex(idx)
        setMode('edit')
    }

    // Submit handler
    const onSubmit = async (data) => {
        if (!content) {
            toast.error('Isi mading wajib diisi')
            return
        }
        setSaving(true)
        let tanggal = ''
        try {
            tanggal = new Date(data.MadingDataTanggal).toISOString()
        } catch {
            tanggal = ''
        }
        const payload = {
            MadingDataJudul: data.MadingDataJudul,
            MadingDataPembuat: data.MadingDataPembuat,
            MadingDataTanggal: tanggal,
            MadingDataImage: images,
            MadingDataIsi: content
        }
        try {
            if (mode === 'edit' && editIndex !== null) {
                // Kirim _id ke backend untuk update
                await contentAPI.createPenulisMading({ ...payload, _id: myMadings[editIndex]._id })
                toast.success('Mading berhasil diupdate')
            } else {
                await contentAPI.createPenulisMading(payload)
                toast.success('Mading berhasil ditambahkan')
            }
            setMode('list')
            fetchMyMadings()
            if (onSuccess) onSuccess()
        } catch (error) {
            console.error(error)
            toast.error(error.response?.data?.message || 'Gagal menyimpan mading')
        } finally {
            setSaving(false)
        }
    }

    // Ambil semua mading milik penulis
    const fetchMyMadings = async () => {
        try {
            const res = await contentAPI.getPenulisMading()
            
            const items = res.data.penulisMading?.[0]?.MadingData || []
            setMyMadings(items)
            setMode('list')
        } catch {
            setMyMadings([])
            setMode('new')
        }
    }

    useEffect(() => {
        fetchMyMadings()
        // eslint-disable-next-line
    }, [])

    // Tambahkan fungsi untuk toggle status publikasi
    const handleToggleStatus = async (itemId) => {
        try {
            const response = await contentAPI.togglePublicationStatus(itemId);
            toast.success(response.data.message);
            fetchMyMadings();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal mengubah status publikasi');
        }
    };

    // Handler hapus mading
    const handleDeleteMading = async (item, idx) => {
        const reason = window.prompt('Masukkan alasan penghapusan mading ini:')
        if (!reason) {
            toast.error('Alasan penghapusan tidak boleh kosong')
            return
        }
        try {
            await contentAPI.deletePenulisMadingItem(item._id, reason)
            toast.success('Mading berhasil dihapus')
            fetchMyMadings()
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal menghapus mading')
        }
    }

    return (
        <div className="space-y-8">
            {/* Selamat Datang */}
            <div className="bg-blue-100 border border-blue-300 rounded p-4 mb-4">
                <h2 className="lg:text-xl text-base font-bold mb-1">Selamat Datang di Halaman Mading Penulis!</h2>
                <p className="lg:text-sm text-xs text-gray-700">Di sini Anda dapat membuat atau mengedit mading Anda sendiri. Anda diberikan kebasan membuat cerpen/puisi/informasi/karya tulis dll yang bernuansa islam.</p>                
                {/* <br /> */}
                {/* <p className="lg:text-sm text-xs text-gray-700 underline"><a href="">Tutorial mengisi mading</a></p> */}
            </div>

            {/* List semua mading */}
            {mode === 'list' && (   
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="lg:text-lg font-semibold">Daftar Mading Anda</h3>
                        <button className="btn btn-primary flex items-center" onClick={handleAddNewMading}>
                            <Plus className="w-4 h-4 mr-1" /> Tambah Mading
                        </button>
                    </div>
                    {myMadings.map((item, idx) => (
                        <div key={item._id || idx} className="card lg:mb-4 mb-2">
                            <div className="card-header flex justify-between items-center">
                                <div className='space-y-1'>
                                    <div className="font-semibold lg:text-base text-xs text-center">{item.MadingDataJudul}</div>
                                    <div className="lg:text-sm text-xs text-center text-gray-500">
                                        Dibuat oleh: {item.MadingDataPembuat} | Tanggal: {item.MadingDataTanggal?.slice(0,10)}
                                    </div>
                                    <div className="lg:text-sm text-xs text-center">
                                        Status: <span className={`font-semibold ${
                                            item.publicationStatus === 'published' ? 'text-green-600' : 'text-yellow-600'
                                        }`}>
                                            {item.publicationStatus === 'published' ? 'Terpublikasi' : 'Draft'}
                                        </span>
                                    </div>
                                    {item.deleted && (
                                        <div className="text-red-600 text-center lg:text-sm text-xs mt-2">
                                            <b>Mading ini dihapus.</b><br />
                                            Alasan: {item.deletedReason} <br />
                                            Tanggal dihapus: {new Date(item.deletedAt).toLocaleString()} <br />
                                            <br />
                                            Note: Mading ini tidak sepenuhnya dihapus, <br />Hubung admin untuk recovery mading ini.                                      
                                        </div>
                                    )}
                                </div>
                                {!item.deleted && (
                                    <div className='gap-2 flex flex-wrap mb-2'>
                                        <button
                                            className={`btn ${
                                                item.publicationStatus === 'published' 
                                                    ? 'bg-yellow-500 hover:bg-yellow-600 h-10 py-2 px-4' 
                                                    : 'bg-teal-500 hover:bg-teal-600 h-10 py-2 px-4'
                                            } text-white`}
                                            onClick={() => handleToggleStatus(item._id)}
                                        >
                                            {item.publicationStatus === 'published' ? 'Ubah ke Draft' : 'Publikasikan'}
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => handleEditMading(item, idx)}>
                                            Edit
                                        </button>
                                        <button className="btn btn-danger" onClick={() => handleDeleteMading(item, idx)}>
                                            <Trash2 className="w-4 h-4" /> Hapus
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Form tambah/edit */}
            {(mode === 'new' || mode === 'edit') && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">
                                {mode === 'edit' ? 'Edit Data Mading' : 'Buat Data Mading Baru'}
                            </h3>
                            <p className="card-description">
                                {mode === 'edit'
                                    ? 'Perbarui data mading Anda di bawah ini.'
                                    : 'Isi data mading sesuai dokumentasi kegiatan'}
                            </p>
                        </div>
                        <div className="card-content space-y-4">
                            <div>
                                <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                    Judul Mading
                                </label>
                                <input
                                    {...register('MadingDataJudul', { required: 'Judul wajib diisi' })}
                                    className="input"
                                    placeholder="Judul mading"
                                />
                                {errors.MadingDataJudul && (
                                    <p className="text-red-500 text-sm mt-1">{errors.MadingDataJudul.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                    Nama Pembuat
                                </label>
                                <input
                                    {...register('MadingDataPembuat', { required: 'Nama pembuat wajib diisi' })}
                                    className="input"
                                    placeholder="Nama pembuat"
                                />
                                {errors.MadingDataPembuat && (
                                    <p className="text-red-500 text-sm mt-1">{errors.MadingDataPembuat.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                    Tanggal Pembuatan
                                </label>
                                <input
                                    type="date"
                                    {...register('MadingDataTanggal', { required: 'Tanggal wajib diisi' })}
                                    className="input"
                                />
                                {errors.MadingDataTanggal && (
                                    <p className="text-red-500 text-sm mt-1">{errors.MadingDataTanggal.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                    Dokumentasi Gambar (bisa lebih dari satu) <br />
                                    <br />
                                    
                                    <span className=''>Note: Jangan lakukan aktivitas lain disaat proses upload gambar berlangsung!</span>
                                </label>
                                <MultiImageUpload
                                    images={images}
                                    onUpload={handleMultiImageUpload}
                                    onRemove={handleRemoveMultiImage}
                                    maxFiles={10}
                                    maxSize={5 * 1024 * 1024} // 5MB
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <div className='flex items-center justify-between mb-2'>
                                <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                    Isi Mading 
                                </label>
                                <label className="lg:block hidden text-sm font-medium text-gray-700 mb-2">
                                    Preview ⬇️
                                </label>
                                </div>
                                <MDEditor
                                    className="w-md-editor"
                                    value={content}
                                    onChange={setContent}
                                    preview="live"
                                    height={400}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            setContent(prev => prev + '  \n');
                                        }
                                    }}
                                />
                                {!content && (
                                    <p className="text-red-500 text-sm mt-1">Isi mading wajib diisi</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn btn-primary"
                        >
                            {saving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4 mr-2" />}
                            {saving
                                ? (mode === 'edit' ? 'Menyimpan...' : 'Menyimpan...')
                                : (mode === 'edit' ? 'Simpan Perubahan' : 'Simpan Mading')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary ml-2"
                            onClick={() => setMode('list')}
                            disabled={saving}
                        >
                            Batal
                        </button>
                    </div>
                </form>
            )}
        </div>
    )
}

export default memo(Mading)